import React, { Dispatch, SetStateAction } from "react";
import { QuestionProps } from "./Questions/Props";

import type { SupportedLanguage } from '@aidkitorg/i18n/lib';
import type { PublicConfig, LoggedInConfig as _LoggedInConfig } from "@aidkitorg/types/lib/config";
import { Call, Device } from "@twilio/voice-sdk";
import { SyncState, SyncStatus } from "./offline/routes";
import { ToJson } from "@aidkitorg/types/lib/endpoints";
export { SupportedLanguage }

export type LoggedInConfig = ToJson<_LoggedInConfig>;

export type ForbiddenCopy = undefined | Record<string, Record<SupportedLanguage, string | undefined>>;

const initialInterfaceContext: {
  activeRequests: Set<string>;
  setRequestActive: (req: string, active: boolean) => void;
  lang: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  textAlign: "left" | "right" | "center";
  direction?: "rtl" | "ltr";
  banner?: Record<SupportedLanguage | '__style', string>;
  setBanner: (banner: Record<SupportedLanguage, string>) => void;
  staffBanner?: Record<SupportedLanguage | '__style', string>;
  setStaffBanner: (banner: Record<SupportedLanguage, string>) => void;
  loginBanner?: Record<SupportedLanguage | 'color', string>;
  setLoginBanner: (banner: Record<SupportedLanguage, string>) => void;
  globalBanner?: Record<SupportedLanguage | '__style', string>;
  setGlobalBanner: (banner: Record<SupportedLanguage | '__style', string>) => void;
  forbiddenCopy?: ForbiddenCopy;
  setForbiddenCopy: Dispatch<SetStateAction<ForbiddenCopy>>;
  audioPlayer?: HTMLAudioElement;
  setAudioPlayer: Dispatch<SetStateAction<HTMLAudioElement | undefined>>;
  localStorage: Storage,
  sessionStorage: Storage
} = {
  activeRequests: new Set(),
  setRequestActive: (req, active) => {},
  lang: "en",
  setLanguage: (lang: string) => {},
  textAlign: "left",
  setBanner: (banner: Record<SupportedLanguage, string>) => {},
  setStaffBanner: (banner: Record<SupportedLanguage, string>) => {},
  setLoginBanner: (banner: Record<SupportedLanguage, string>) => {},
  setGlobalBanner: (banner: Record<SupportedLanguage, string>) => {},
  setForbiddenCopy: () => {},
  setAudioPlayer: () => {},
  localStorage: {} as Storage,
  sessionStorage: {} as Storage,
};
const InterfaceContext = React.createContext(initialInterfaceContext);

export const PublicConfigurationContext = React.createContext<Partial<PublicConfig>>({});
// The value for loggedIn context will be undefined if the user is not logged in.
export const LoggedInConfigurationContext: React.Context<LoggedInConfig | undefined> = React.createContext<LoggedInConfig | undefined>(undefined);

const initialConfigurationContext: {
  [key: string]: any;
} = {};
export const ConfigurationContext = React.createContext(initialConfigurationContext)

export const TwilioDeviceContext = React.createContext({} as {
  twilioDevice: Device | null;
  twilioCall: Call | null,
  twilioCallDuration: string,
  twilioCaller: string | null,
  setTwilioCall: (call: Call | null) => void;
  twilioVoiceChannel: string,
  resetTwilioVoiceChannel: (channel: string) => Promise<void>;
  cleanupTwilioDevice: () => Promise<void>;
});

export const SurveyContext = React.createContext({} as { 
  sections: Array<QuestionProps & { 'Questions': QuestionProps[] }>, 
  stages?: string[],
  expire_date?: string,
  is_dashboard?: boolean
});

export const AuthContext = React.createContext({
  setToken() {},
  setLocalId() {}
} as {
  token?: () => string;
  setToken: (token: string | null) => void;
  localId?: () => string;  
  setLocalId: (localId: string) => void;
});

// Raw subsurvey link key, provided by SubsectionPage and read in useToken as a fallback
// so leaf components that remount after the cookie is trimmed can still recover it.
export const LinkKeyContext = React.createContext<string | undefined>(undefined);

export const UserInfoContext = React.createContext({} as {
  uid?: string,
  tags?: string[],
  name?: string,
  // The current user's own user group id (string), or undefined if they belong
  // to none. Used to prevent sharing a collection with a group you're not in.
  user_group?: string,
  refreshContext?: () => void,
});

export type OfflineStatus = 'unloaded'|'warming'|'warmed'|'syncing'|'synced'|'failed';

export const OnlineAwarenessContext = React.createContext({ 
  online: true,
  status: 'unloaded',
} as { 
  online: boolean,
  status: OfflineStatus
});

export const OfflineSyncContext = React.createContext({
  statuses: {}
} as {
  statuses: Record<string, SyncState>
});

export type ScrollToTopContext = {
  scrollToTop: () => void;
  scrollContainerRef: React.RefObject<HTMLElement>;
};

export const ScrollToTopContext = React.createContext<ScrollToTopContext | undefined>(undefined);

export default InterfaceContext;
